import { useState, useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const [openCollapses, setOpenCollapses] = useState({});

  // Improved active route detection
  const isRouteActive = (route) => {
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  };

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const toggleCollapse = (key) => {
    setOpenCollapses((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Auto-expand parent when child is active
  useEffect(() => {
    const newOpenCollapses = {};
    routes.forEach((route) => {
      if (route.collapse) {
        const hasActiveChild = route.collapse.some((item) => isRouteActive(item.route));
        newOpenCollapses[route.key] = hasActiveChild;
      }
    });
    setOpenCollapses(newOpenCollapses);
  }, [location.pathname]);

  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route, collapse }) => {
      let returnValue;

      if (type === "collapse") {
        const isActive =
          isRouteActive(route) || (collapse && collapse.some((item) => isRouteActive(item.route)));

        if (collapse) {
          // Handle dropdown items (like Accounts)
          returnValue = (
            <div key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                active={isActive}
                noCollapse={noCollapse}
                onClick={() => toggleCollapse(key)}
                isOpen={openCollapses[key]}
                hasSubitems={!!collapse}
              />
              <Collapse in={openCollapses[key] || isActive} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 2 }}>
                  {collapse.map((subItem) => {
                    const isSubItemActive = isRouteActive(subItem.route);
                    return (
                      <NavLink
                        key={subItem.key}
                        to={subItem.route}
                        style={{ textDecoration: "none" }}
                      >
                        <SidenavCollapse
                          name={subItem.name}
                          icon={subItem.icon}
                          active={isSubItemActive}
                          nested={true}
                        />
                      </NavLink>
                    );
                  })}
                </List>
              </Collapse>
            </div>
          );
        } else {
          // Handle regular collapse items
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse name={name} icon={icon} active={isActive} noCollapse={noCollapse} />
            </Link>
          ) : (
            <NavLink key={key} to={route} style={{ textDecoration: "none" }}>
              <SidenavCollapse name={name} icon={icon} active={isActive} noCollapse={noCollapse} />
            </NavLink>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          href="https://www.bitstechnologygh.com"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth
        >
          contact administrator
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
